import LgpdNotice from '@/components/feature/LgpdNotice';
import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const services = [
  {
    icon: 'ri-building-line',
    title: 'Eventos Corporativos',
    description: 'Soluções completas para convenções, workshops e treinamentos empresariais com múltiplos canais de áudio simultâneos. Ideal para empresas que buscam inovação e eficiência em seus eventos.',
    features: [
      'Até 5 canais simultâneos',
      'Suporte para 5000+ participantes',
      'Instalação e desmontagem incluídas',
      'Suporte técnico presencial',
      'Gravação de todas as sessões'
    ]
  },
  {
    icon: 'ri-global-line',
    title: 'Conferências Multilíngues',
    description: 'Sistema profissional de tradução simultânea em até 5 idiomas, perfeito para eventos internacionais. Elimine barreiras linguísticas e conecte participantes de todo o mundo.',
    features: [
      'Tradução em até 5 idiomas',
      'Tradutores profissionais certificados',
      'Qualidade de áudio excepcional',
      'Sem necessidade de cabines',
      'Flexibilidade total de idiomas'
    ]
  },
  {
    icon: 'ri-team-line',
    title: 'Workshops Interativos',
    description: 'Tecnologia que permite múltiplas sessões no mesmo espaço, otimizando recursos e aumentando engajamento. Perfeito para eventos com diferentes trilhas de conteúdo.',
    features: [
      'Múltiplas trilhas simultâneas',
      'Participantes transitam livremente',
      'Economia de espaço físico',
      'Maior engajamento',
      'Análise de participação'
    ]
  },
  {
    icon: 'ri-music-line',
    title: 'Festas Silent Disco',
    description: 'Experiência única para eventos sociais corporativos com vários DJs simultâneos e iluminação LED nos fones. Transforme confraternizações em experiências memoráveis.',
    features: [
      'Vários DJs simultâneos',
      'Fones com LED colorido',
      'Sem poluição sonora',
      'Perfeito para ambientes urbanos',
      'Experiência diferenciada'
    ]
  },
  {
    icon: 'ri-presentation-line',
    title: 'Palestras e Seminários',
    description: 'Solução ideal para palestras motivacionais, seminários técnicos e apresentações corporativas. Garanta que cada palavra seja ouvida com clareza cristalina.',
    features: [
      'Áudio de alta fidelidade',
      'Alcance de até 500 metros',
      'Bateria de longa duração',
      'Fácil operação',
      'Backup de equipamentos'
    ]
  },
  {
    icon: 'ri-graduation-cap-line',
    title: 'Treinamentos Corporativos',
    description: 'Otimize programas de capacitação com múltiplos níveis simultâneos. Treine mais pessoas em menos tempo, mantendo a qualidade e personalização do conteúdo.',
    features: [
      'Níveis simultâneos (iniciante/avançado)',
      'Economia de tempo',
      'Maior aproveitamento',
      'Conteúdo personalizado',
      'Relatórios de participação'
    ]
  }
];

const benefits = [
  {
    icon: 'ri-volume-mute-line',
    title: 'Zero Poluição Sonora',
    description: 'Realize eventos em qualquer horário e local sem incomodar vizinhos ou violar regulamentações de ruído.'
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Economia Comprovada',
    description: 'Reduza custos em até 50% com locação de espaços, equipamentos e logística operacional.'
  },
  {
    icon: 'ri-group-line',
    title: 'Múltiplas Sessões',
    description: 'Realize até 5 palestras simultâneas no mesmo ambiente, otimizando espaço e recursos.'
  },
  {
    icon: 'ri-translate-2',
    title: 'Tradução Integrada',
    description: 'Ofereça múltiplos idiomas simultaneamente sem necessidade de cabines ou equipamentos extras.'
  },
  {
    icon: 'ri-settings-3-line',
    title: 'Controle Individual',
    description: 'Cada participante ajusta volume e escolhe o canal desejado, garantindo conforto total.'
  },
  {
    icon: 'ri-leaf-line',
    title: 'Sustentável',
    description: 'Menor consumo de energia e equipamentos recicláveis, contribuindo para eventos mais sustentáveis.'
  }
];

export default function ServicesPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://ordem-de-servico.stibpalestrasilenciosasp.com.br/pedido.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Solicitação enviada com sucesso! Entraremos em contato em breve.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });
      } else {
        setSubmitMessage('Erro ao enviar. Por favor, tente novamente.');
      }
    } catch (error) {
      setSubmitMessage('Erro ao enviar. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 8000);
    }
  };

  useEffect(() => {
    document.title = 'Serviços Palestra Silenciosa São Paulo | Eventos Corporativos | STIB';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Serviços de palestra silenciosa em São Paulo: eventos corporativos, conferências multilíngues, workshops, silent disco e treinamentos. Aluguel de fones sem fio. Orçamento: (11) 91777-6227');
    }

    const lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) {
      lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
    }

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://stibpalestrasilenciosasp.com.br';

    // Add page-specific schema
    const serviceSchema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: services.map((service, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Service',
          name: service.title,
          description: service.description,
          provider: {
            '@type': 'LocalBusiness',
            name: 'STIB - Palestra Silenciosa São Paulo'
          }
        }
      }))
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'services-schema';
    script.text = JSON.stringify(serviceSchema);
    document.head.appendChild(script);

    // BreadcrumbList schema
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Serviços', item: `${siteUrl}/o-que-fazemos` }
      ]
    };

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.id = 'services-breadcrumb-schema';
    breadcrumbScript.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    return () => {
      const existingScript = document.getElementById('services-schema');
      if (existingScript) existingScript.remove();
      const existingBreadcrumb = document.getElementById('services-breadcrumb-schema');
      if (existingBreadcrumb) existingBreadcrumb.remove();
    };
  }, []);

  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: servicesRef, isVisible: servicesVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: equipRef, isVisible: equipVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: benefitsRef, isVisible: benefitsVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation({ threshold: 0.2 });

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative h-[500px] flex items-center justify-center overflow-hidden mt-20" aria-label="Serviços de Palestra Silenciosa São Paulo">
          <div className="absolute inset-0">
            <img
              src="/img/stib/equip-console.webp"
              alt="Serviços de Palestra Silenciosa São Paulo - Equipamentos de áudio sem fio para eventos corporativos"
              title="Serviços STIB - Palestra Silenciosa São Paulo"
              className="w-full h-full object-cover object-top animate-hero-zoom"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-secondary/70 via-secondary/50 to-secondary/70 dark:from-black/80 dark:via-black/60 dark:to-black/80"></div>
          </div>

          {/* Breadcrumb — posicionado no topo do hero */}
          <div className="absolute top-6 left-0 right-0 z-20">
            <div className="max-w-7xl mx-auto px-6">
              <Breadcrumb
                variant="light"
                items={[
                  { label: 'Home', to: '/' },
                  { label: 'Serviços' },
                ]}
              />
            </div>
          </div>

          <div ref={heroRef} className="relative z-10 text-center px-6">
            <h1 className={`font-heading font-bold text-6xl md:text-7xl text-white mb-6 transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              NOSSOS SERVIÇOS
            </h1>
            <p className={`font-body text-xl text-white/90 max-w-2xl mx-auto transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              Soluções completas de <strong>áudio sem fio</strong> para todos os tipos de <strong>eventos corporativos</strong>
            </p>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-28 bg-white dark:bg-gray-900" aria-label="Lista de serviços">
          <div className="max-w-7xl mx-auto px-6">
            <div ref={servicesRef} className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {services.map((service, index) => (
                <div
                  key={index}
                  className={`bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 rounded-3xl p-10 hover:border-primary transition-all duration-500 hover:-translate-y-1 ${servicesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                  style={{ transitionDelay: `${index * 80}ms` }}
                >
                  <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gradient-to-br from-primary to-secondary mb-6 transition-transform duration-500 hover:scale-110">
                    <i className={`${service.icon} text-white text-4xl`}></i>
                  </div>
                  <h3 className="font-heading font-bold text-3xl text-secondary dark:text-white mb-4">
                    {service.title}
                  </h3>
                  <p className="font-body text-base text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <i className="ri-check-line text-primary text-xl flex-shrink-0 mt-1"></i>
                        <span className="font-body text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contato"
                    className="inline-block w-full text-center bg-secondary dark:bg-primary text-white px-8 py-4 rounded-full font-body font-semibold hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
                  >
                    Solicitar Orçamento
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Equipment Section */}
        <section className="py-28 bg-gray-50 dark:bg-gray-800" aria-label="Nossos equipamentos">
          <div className="max-w-7xl mx-auto px-6">
            <div ref={equipRef} className="text-center mb-16">
              <span className={`font-body text-primary font-semibold text-sm uppercase tracking-widest mb-3 block transition-all duration-700 ${equipVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                Tecnologia de Ponta
              </span>
              <h2 className={`font-heading font-bold text-5xl md:text-6xl text-secondary dark:text-white mb-6 transition-all duration-700 delay-100 ${equipVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                NOSSOS EQUIPAMENTOS
              </h2>
              <p className={`font-body text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto transition-all duration-700 delay-200 ${equipVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                Utilizamos equipamentos profissionais de última geração para garantir a melhor
                experiência sonora em todos os seus eventos
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Equipamento 1 */}
              <div className={`bg-white dark:bg-gray-900 rounded-3xl overflow-hidden border border-gray-100 dark:border-gray-700 hover:border-primary/40 transition-all duration-500 group ${equipVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`} style={{ transitionDelay: '200ms' }}>
                <div className="relative w-full h-80 overflow-hidden">
                  <img
                    src="/img/stib/fone-led-stib.webp"
                    alt="Fones de ouvido sem fio profissionais para eventos silenciosos - STIB"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                  <span className="absolute bottom-4 left-4 bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full font-body uppercase tracking-wider">
                    Fones Sem Fio
                  </span>
                </div>
                <div className="p-8">
                  <h3 className="font-heading font-bold text-2xl text-secondary dark:text-white mb-3">
                    Headphones Profissionais
                  </h3>
                  <p className="font-body text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
                    Fones de ouvido sem fio de alta fidelidade com LED colorido para identificação
                    de canal, bateria de longa duração e controle individual de volume. Confortáveis
                    para uso prolongado em eventos de todos os portes.
                  </p>
                  <ul className="space-y-3">
                    {[
                      'Alcance de até 200 metros',
                      'Bateria de 10+ horas de uso',
                      'LED colorido por canal',
                      'Controle de volume individual',
                      'Higienizados entre cada uso',
                    ].map((spec, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-primary text-base"></i>
                        </div>
                        <span className="font-body text-sm text-gray-700 dark:text-gray-300">{spec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Equipamento 2 */}
              <div className={`bg-white dark:bg-gray-900 rounded-3xl overflow-hidden border border-gray-100 dark:border-gray-700 hover:border-primary/40 transition-all duration-500 group ${equipVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`} style={{ transitionDelay: '400ms' }}>
                <div className="relative w-full h-80 overflow-hidden">
                  <img
                    src="/img/stib/equip-microfones.webp"
                    alt="Transmissores e receptores de áudio sem fio para eventos corporativos - STIB"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                  <span className="absolute bottom-4 left-4 bg-secondary dark:bg-primary text-white dark:text-black text-xs font-semibold px-3 py-1 rounded-full font-body uppercase tracking-wider">
                    Transmissores
                  </span>
                </div>
                <div className="p-8">
                  <h3 className="font-heading font-bold text-2xl text-secondary dark:text-white mb-3">
                    Transmissores de Áudio
                  </h3>
                  <p className="font-body text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
                    Transmissores de alta potência com tecnologia FM digital de última geração,
                    garantindo cobertura total do ambiente sem interferências. Suporte a até 5
                    canais independentes e simultâneos para máxima flexibilidade.
                  </p>
                  <ul className="space-y-3">
                    {[
                      'Até 5 canais simultâneos',
                      'Tecnologia FM digital',
                      'Cobertura de grandes áreas',
                      'Sem interferência de sinal',
                      'Instalação rápida e simples',
                    ].map((spec, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-primary text-base"></i>
                        </div>
                        <span className="font-body text-sm text-gray-700 dark:text-gray-300">{spec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Equipment note */}
            <div className={`mt-12 bg-primary/5 dark:bg-primary/10 border border-primary/20 rounded-2xl px-8 py-6 flex items-start gap-4 transition-all duration-700 delay-500 ${equipVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-primary text-2xl"></i>
              </div>
              <p className="font-body text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                <strong className="text-secondary dark:text-white">Todo o equipamento é fornecido e instalado pela nossa equipe.</strong>{' '}
                Não é necessário que o contratante providencie nenhum item. Realizamos a montagem antes do
                evento e a desmontagem ao final, com suporte técnico presencial durante toda a duração.
              </p>
            </div>
          </div>
        </section>

        {/* Aluguel de Fones Sem Fio — SEO cross-link block */}
        <section className="py-20 bg-white dark:bg-gray-900 border-t border-b border-gray-100 dark:border-gray-800">
          <div className="max-w-7xl mx-auto px-6">
            <div className="bg-gradient-to-r from-secondary to-secondary/90 dark:from-gray-800 dark:to-gray-700 rounded-3xl p-10 md:p-14 flex flex-col lg:flex-row items-center gap-10">
              <div className="flex-1">
                <span className="inline-block bg-primary text-black text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
                  Locação de Equipamentos
                </span>
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
                  Aluguel de Fones Sem Fio para Eventos
                </h2>
                <p className="font-body text-white/85 leading-relaxed mb-6 max-w-xl">
                  Precisa apenas dos equipamentos? Oferecemos <strong className="text-white">aluguel de fones sem fio</strong> com transmissores, microfones, montagem, suporte técnico presencial e 10% de fones extras de reserva. Atendemos todo o Brasil.
                </p>
                <div className="flex flex-wrap gap-3 mb-6">
                  <span className="inline-flex items-center gap-1.5 bg-white/10 text-white text-sm px-3 py-1.5 rounded-full">
                    <i className="ri-headphone-line text-primary"></i> +5.000 fones
                  </span>
                  <span className="inline-flex items-center gap-1.5 bg-white/10 text-white text-sm px-3 py-1.5 rounded-full">
                    <i className="ri-battery-charge-line text-primary"></i> 10–12h bateria
                  </span>
                  <span className="inline-flex items-center gap-1.5 bg-white/10 text-white text-sm px-3 py-1.5 rounded-full">
                    <i className="ri-signal-tower-line text-primary"></i> Alcance 500m
                  </span>
                </div>
                <Link
                  to="/aluguel-fones-sem-fio"
                  className="inline-flex items-center gap-3 bg-primary text-black px-8 py-4 rounded-full font-body font-bold text-base uppercase hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-headphone-line text-lg"></i>
                  Ver Pacotes de Aluguel
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="flex-shrink-0 w-full lg:w-80 h-64 lg:h-72 rounded-2xl overflow-hidden">
                <img
                  src="/img/stib/fone-kit-maleta.webp"
                  alt="Aluguel de fones sem fio para eventos — equipamentos profissionais STIB"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-28 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-6">
            <div ref={benefitsRef} className="text-center mb-16">
              <h2 className={`font-heading font-bold text-5xl md:text-6xl text-secondary dark:text-white mb-6 transition-all duration-700 ${benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                POR QUE ESCOLHER NOSSA TECNOLOGIA
              </h2>
              <p className={`font-body text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto transition-all duration-700 delay-100 ${benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                Benefícios que transformam a experiência dos seus eventos
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className={`bg-white dark:bg-gray-900 rounded-2xl p-8 transition-all duration-500 hover:-translate-y-1 border border-transparent hover:border-primary/20 ${benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                  style={{ transitionDelay: `${index * 80}ms` }}
                >
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20 mb-6 transition-transform duration-500 hover:scale-110">
                    <i className={`${benefit.icon} text-primary text-3xl`}></i>
                  </div>
                  <h3 className="font-heading font-bold text-xl text-secondary dark:text-white mb-3">
                    {benefit.title}
                  </h3>
                  <p className="font-body text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Blog Internal Links */}
        <section className="py-20 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="font-body text-primary font-semibold text-sm uppercase tracking-widest mb-3 block">
                Aprofunde seu Conhecimento
              </span>
              <h2 className="font-heading font-bold text-4xl text-secondary dark:text-white">
                Leia Nossos Guias Completos
              </h2>
              <p className="font-body text-gray-600 dark:text-gray-300 mt-4 max-w-xl mx-auto">
                Entenda melhor como a tecnologia de palestra silenciosa pode transformar seus eventos
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { to: '/blog/palestra-silenciosa-o-que-e-como-funciona-guia-completo', img: '/img/stib/fone-plateia-media.webp', label: 'Palestra Silenciosa: O Que É, Como Funciona e Por Que Seu Evento Precisa Disso', time: '12 min' },
                { to: '/blog/conferencia-silenciosa-silent-conference-brasil-guia-completo', img: '/img/stib/fone-auditorio-alto.webp', label: 'Conferência Silenciosa no Brasil: O Guia Definitivo para um Silent Conference de Sucesso', time: '14 min' },
                { to: '/blog/silent-disco-brasil-festa-silenciosa-guia-completo', img: '/img/stib/festa-silent-disco.webp', label: 'Silent Disco no Brasil: O Guia Completo sobre Festa Silenciosa', time: '13 min' },
              ].map((item, i) => (
                <Link key={i} to={item.to} className="group flex gap-6 bg-gray-50 dark:bg-gray-800 rounded-2xl p-6 hover:bg-white dark:hover:bg-gray-700 border-2 border-transparent hover:border-primary/20 dark:border-gray-700 transition-all duration-300 cursor-pointer hover:-translate-y-1">
                  <div className="flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden">
                    <img src={item.img} alt={item.label} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="inline-block text-xs font-semibold text-primary bg-primary/10 dark:bg-primary/20 px-2 py-1 rounded-full mb-2">
                    Guia Completo
                    </span>
                    <h3 className="font-heading font-bold text-base text-secondary dark:text-white group-hover:text-primary transition-colors duration-300 leading-snug mb-2">
                    {item.label}
                    </h3>
                    <p className="font-body text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    <i className="ri-time-line"></i> {item.time} de leitura
                    </p>
                  </div>
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20 group-hover:bg-primary transition-all duration-300 self-center">
                    <i className="ri-arrow-right-line text-primary group-hover:text-white text-sm transition-colors duration-300"></i>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="py-28 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="font-body text-primary font-semibold text-sm uppercase tracking-widest mb-4 block">
                  Fale Conosco
                </span>
                <h2 className="font-heading font-bold text-5xl text-secondary dark:text-white mb-6">
                  Solicite Seu Orçamento
                </h2>
                <p className="font-body text-lg text-gray-600 dark:text-gray-300 leading-relaxed mb-8">
                  Preencha o formulário e nossa equipe entrará em contato para entender suas necessidades e criar uma proposta personalizada.
                </p>
                
                <div className="space-y-6">
                  <a 
                    href="mailto:contato@stibpalestrasilenciosasp.com.br"
                    className="flex items-center gap-4 group cursor-pointer"
                  >
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20 group-hover:bg-primary transition-all duration-300">
                      <i className="ri-mail-line text-primary group-hover:text-white text-2xl transition-all duration-300"></i>
                    </div>
                    <div>
                      <p className="font-body text-sm text-gray-500 dark:text-gray-400">E-mail</p>
                      <p className="font-body font-semibold text-secondary dark:text-white">contato@stibpalestrasilenciosasp.com.br</p>
                    </div>
                  </a>
                  
                  <a 
                    href="https://wa.me/5511917776227?text=Olá! Gostaria de saber mais sobre os serviços de palestra silenciosa."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 group cursor-pointer"
                  >
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30 group-hover:bg-green-500 transition-all duration-300">
                      <i className="ri-whatsapp-line text-green-500 group-hover:text-white text-2xl transition-all duration-300"></i>
                    </div>
                    <div>
                      <p className="font-body text-sm text-gray-500 dark:text-gray-400">WhatsApp</p>
                      <p className="font-body font-semibold text-secondary dark:text-white">(11) 91777-6227</p>
                    </div>
                  </a>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 rounded-3xl p-10">
                <form onSubmit={handleSubmit} data-readdy-form id="services-contact-form">
                  <div className="space-y-5">
                    <div>
                      <label className="block font-body text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Nome Completo *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-lg focus:border-primary focus:outline-none font-body bg-white dark:bg-gray-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block font-body text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        E-mail *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-lg focus:border-primary focus:outline-none font-body bg-white dark:bg-gray-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block font-body text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Telefone *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-lg focus:border-primary focus:outline-none font-body bg-white dark:bg-gray-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block font-body text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Serviço de Interesse *
                      </label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-lg focus:border-primary focus:outline-none font-body bg-white dark:bg-gray-900 dark:text-white"
                      >
                        <option value="">Selecione...</option>
                        <option value="Eventos Corporativos">Eventos Corporativos</option>
                        <option value="Conferências Multilíngues">Conferências Multilíngues</option>
                        <option value="Workshops Interativos">Workshops Interativos</option>
                        <option value="Festas Silent Disco">Festas Silent Disco</option>
                        <option value="Palestras e Seminários">Palestras e Seminários</option>
                        <option value="Treinamentos Corporativos">Treinamentos Corporativos</option>
                      </select>
                    </div>

                    <div>
                      <label className="block font-body text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Mensagem
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        maxLength={500}
                        rows={3}
                        className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-lg focus:border-primary focus:outline-none font-body resize-none bg-white dark:bg-gray-900 dark:text-white"
                        placeholder="Conte-nos mais sobre seu evento..."
                      ></textarea>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {formData.message.length}/500 caracteres
                      </p>
                    </div>

                    <LgpdNotice kind="orcamento" className="mb-4 col-span-full" />
            <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-primary text-white px-8 py-4 rounded-full font-body font-bold text-base hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'ENVIANDO...' : 'SOLICITAR ORÇAMENTO'}
                    </button>

                    {submitMessage && (
                      <p className={`text-center font-body text-sm ${submitMessage.includes('sucesso') ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {submitMessage}
                      </p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-28 bg-secondary dark:bg-gray-800">
          <div ref={ctaRef} className="max-w-4xl mx-auto px-6 text-center">
            <h2 className={`font-heading font-bold text-5xl md:text-6xl text-white mb-8 transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              Pronto para Transformar Seus Eventos?
            </h2>
            <p className={`font-body text-xl text-white/90 mb-12 max-w-2xl mx-auto transition-all duration-700 delay-150 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              Entre em contato e descubra como nossa tecnologia pode revolucionar seus eventos corporativos
            </p>
            <Link
              to="/contato"
              className={`inline-flex items-center gap-4 bg-primary text-black px-12 py-5 rounded-full font-body font-bold text-lg uppercase hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: '300ms' }}
            >
              SOLICITAR ORÇAMENTO
              <div className="w-10 h-10 flex items-center justify-center bg-white rounded-full">
                <i className="ri-arrow-right-line text-black text-xl"></i>
              </div>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
